import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import NoiseMatrixEditor from "@/components/spots-admin/NoiseMatrixEditor";
import type { NoiseMatrix } from "@/utils/noiseMatrix";

const CATEGORIES = [
  { value: "cafe", label: "Cafe" },
  { value: "gym", label: "Gym" },
  { value: "hotel_lobby", label: "Hotel Lobby" },
  { value: "coworking", label: "Coworking" },
  { value: "library", label: "Library" },
  { value: "restaurant", label: "Restaurant" },
  { value: "other", label: "Other" },
];

const ACCESS_TYPES = [
  { value: "free", label: "Free" },
  { value: "purchase_required", label: "Purchase Required" },
  { value: "members_only", label: "Members Only" },
];

export interface SpotEditData {
  category: string;
  access_type: string;
  wifi_available: boolean;
  power_outlets: boolean;
  noise_matrix: NoiseMatrix | null;
  description: string | null;
  admin_notes: string | null;
  is_active: boolean;
}

interface SpotEditModalProps {
  spot: {
    id: string;
    name: string;
    category: string;
    access_type: string;
    wifi_available: boolean;
    power_outlets: boolean;
    noise_matrix?: NoiseMatrix | null;
    description?: string | null;
    admin_notes?: string | null;
    is_active: boolean;
  };
  onClose: () => void;
  onSave: (id: string, data: Partial<SpotEditData>) => Promise<void>;
}

export default function SpotEditModal({ spot, onClose, onSave }: SpotEditModalProps) {
  const [category, setCategory] = useState(spot.category);
  const [accessType, setAccessType] = useState(spot.access_type);
  const [wifiAvailable, setWifiAvailable] = useState(spot.wifi_available);
  const [powerOutlets, setPowerOutlets] = useState(spot.power_outlets);
  const [noiseMatrix, setNoiseMatrix] = useState<NoiseMatrix | null>(spot.noise_matrix || null);
  const [description, setDescription] = useState(spot.description || "");
  const [adminNotes, setAdminNotes] = useState(spot.admin_notes || "");
  const [isActive, setIsActive] = useState(spot.is_active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(spot.id, {
        category,
        access_type: accessType,
        wifi_available: wifiAvailable,
        power_outlets: powerOutlets,
        noise_matrix: noiseMatrix,
        description: description || null,
        admin_notes: adminNotes || null,
        is_active: isActive,
      });
    } catch {
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectClass =
    "w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  const textareaClass =
    "w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-foreground/40" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card rounded-t-xl">
          <h2 className="font-bold text-foreground text-lg truncate pr-4">{spot.name}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Category */}
          <label className="block space-y-1">
            <span className="text-sm font-medium text-foreground">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          {/* Access Type */}
          <label className="block space-y-1">
            <span className="text-sm font-medium text-foreground">Access Type</span>
            <select value={accessType} onChange={(e) => setAccessType(e.target.value)} className={selectClass}>
              {ACCESS_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </label>

          {/* Noise Matrix */}
          <NoiseMatrixEditor value={noiseMatrix} onChange={setNoiseMatrix} />

          {/* Toggles */}
          <div className="space-y-3">
            <Toggle label="WiFi Available" checked={wifiAvailable} onChange={setWifiAvailable} />
            <Toggle label="Power Outlets" checked={powerOutlets} onChange={setPowerOutlets} />
            <Toggle label="Visible to users" checked={isActive} onChange={setIsActive} />
          </div>

          {/* Description */}
          <label className="block space-y-1">
            <span className="text-sm font-medium text-foreground">Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={textareaClass} placeholder="Optional description…" />
          </label>

          {/* Admin Notes */}
          <label className="block space-y-1">
            <span className="text-sm font-medium text-foreground">Admin Notes</span>
            <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} className={textareaClass} placeholder="Internal notes…" />
          </label>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-card rounded-b-xl">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${
          checked ? "bg-primary" : "bg-muted-foreground/30"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}
